import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import Loading from "../../common/components/loading/loading";
import {
  appendBandTooltipTargets,
  buildTooltipHtml,
  createTooltipHandlers,
  formatTooltipCurrency,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const AesoYearlyAveragePoolPriceGraph = ({ poolPriceData, isLoading }) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    if (!svg || !svg.node()) return;
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    // Calculate dimensions based on the size of the parent container
    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight;
    const margin = { top: 40, right: 10, bottom: 40, left: 50 };

    // Set dimensions considering margins
    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    // Create SVG
    const chartSvg = svg
      .attr("width", parentWidth)
      .attr("height", parentHeight)
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none") // Preserve aspect ratio
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    // Parse dates and average by year
    const parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%SZ");
    const formatYear = d3.timeFormat("%Y");

    const averageByYear = (data) => {
      const groupedData = d3.group(data, (d) => {
        const parsedDate = parseDate(d.marketDate);
        if (!parsedDate) {
          //console.error(`Failed to parse date: ${d.marketDate}`);
          return null;
        }
        return formatYear(parsedDate);
      });

      return Array.from(groupedData, ([key, values]) => ({
        year: key,
        avgPoolPrice: d3.mean(values, (d) => +d.poolPrice),
      })).sort((a, b) => d3.ascending(a.year, b.year));
    };

    const yearlyData = averageByYear(poolPriceData);

    // Set up scales
    const xScale = d3
      .scaleBand()
      .domain(yearlyData.map((d) => d.year))
      .range([0, innerWidth])
      .padding(0.1);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(yearlyData, (d) => d.avgPoolPrice)])
      .nice()
      .range([innerHeight, 0]);

    // Add gridlines
    chartSvg
      .append("g")
      .attr("class", "grid  d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

    // Draw bars
    chartSvg
      .selectAll(".bar")
      .data(yearlyData)
      .enter()
      .append("rect")
      .attr("class", "bar dashboard-bar dashboard-bar-accent")
      .attr("x", (d) => xScale(d.year))
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("y", (d) => yScale(d.avgPoolPrice))
      .attr("width", xScale.bandwidth())
      .attr("height", (d) => innerHeight - yScale(d.avgPoolPrice))
      .attr("fill", "#0096FF");

    // Add X axis
    chartSvg
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .attr("class", "d3-chart-axis")
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end");

    // Add Y axis
    chartSvg
      .append("g")
      .call(d3.axisLeft(yScale).tickFormat((d) => `$${d.toFixed(0)}`))
      .attr("class", "d3-chart-axis");

    // Add Y axis label
    chartSvg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - innerHeight / 2)
      .attr("dy", "1em")
      .attr("class", "d3-chart-axis-label")
      .style("text-anchor", "middle")
      .attr("font-size", "14px")
      .text("Average Pool Price ($/MWh)")
      .attr("fill", "grey");

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    appendBandTooltipTargets({
      chartSvg,
      data: yearlyData,
      xScale,
      innerHeight,
      xAccessor: (d) => d.year,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) =>
        buildTooltipHtml({
          title: String(d.year),
          rows: [
            {
              label: "Average Pool Price",
              value: formatTooltipCurrency(d.avgPoolPrice, 2),
              color: "#0096FF",
            },
          ],
        }),
    });

    // Add title
    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text("Yearly Average Pool Price")
      .attr("class", "d3-chart-title");

    // Add value labels on top of bars
    chartSvg
      .selectAll(".label")
      .data(yearlyData)
      .enter()
      .append("text")
      .attr("class", "label")
      .attr("x", (d) => xScale(d.year) + xScale.bandwidth() / 2)
      .attr("y", (d) => yScale(d.avgPoolPrice) - 5)
      .attr("text-anchor", "middle")
      .attr("font-size", "12px")
      .text((d) => `$${d.avgPoolPrice.toFixed(2)}`)
      .attr("class", "d3-chart-legend-label");
  }, [poolPriceData]);

  useEffect(() => {
    !isLoading && renderChart();
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current; // <- capture current value
    if (!svgNode) return;

    const throttledResize = (entries) => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100); // throttle duration in ms
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;
  return <svg ref={svgRef} className="dashboard-d3-chart" style={{ width: "100%", height: "100%" }}></svg>;
};

export default AesoYearlyAveragePoolPriceGraph;









